import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInAnonymously,
  linkWithPopup,
  onAuthStateChanged,
  onIdTokenChanged,
  setPersistence,
  browserLocalPersistence,
  User,
  AuthError,
} from "firebase/auth";
import { getApps, getApp, initializeApp } from "firebase/app";
import { getUser } from "../apiService";
import { useUserStore } from "../../store/userStore";
import { useCollectionStore } from "../../store/collectionStore";

/* ------------------------------------------------------------------ */
/* Firebase setup */
/* ------------------------------------------------------------------ */
const firebaseConfig = {
  apiKey: process.env.EXPO_PUBLIC_FIREBASE_API_KEY!,
  authDomain: process.env.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN!,
  projectId: process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID!,
  storageBucket: process.env.EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET!,
  messagingSenderId: process.env.EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID!,
  appId: process.env.EXPO_PUBLIC_FIREBASE_APP_ID!,
};

let authInstance: ReturnType<typeof getAuth> | null = null;

let listenersStarted = false;

function initAuthListeners() {
  if (listenersStarted) return;
  listenersStarted = true;
  const auth = getAuthInstance();
  const store = useUserStore.getState();

  onAuthStateChanged(auth, async (user) => {
    const store = useUserStore.getState();

    if (!user) {
      try {
        const anon = await signInAnonymously(auth);
        const syncedUser = await syncUser(anon.user)
        store.setUser(syncedUser);
      } catch (err) {
        console.warn("Anonymous sign-in failed", err);
      }
      return;
    }
    const syncedUser = await syncUser(user)
    store.setUser(syncedUser);
    useCollectionStore.getState().setReload(true);
  });

onIdTokenChanged(auth, async (user) => {
    if (!user) {
      store.reset();
      return;
    }
    const previousUid = store.user?.firebaseUser?.uid;
    const token = await user.getIdToken();
    store.setIdToken(token);
      setTimeout(async () => {
       const syncedUser = await syncUser(user)
       store.setUser(syncedUser);
      }, 100);
    store.setInitialized();
    if (previousUid !== user.uid) {
    useCollectionStore.getState().setReload(true);
    }
  });
}

function getFirebaseApp() {
  return getApps().length ? getApp() : initializeApp(firebaseConfig);
}

function getAuthInstance() {
  if (authInstance) return authInstance;

  const auth = getAuth(getFirebaseApp());
  setPersistence(auth, browserLocalPersistence);
  authInstance = auth;

  return authInstance;
}

/* ------------------------------------------------------------------ */
/* Backend sync */
/* ------------------------------------------------------------------ */
async function syncUser(firebaseUser: User) {
  const { user, firstTimeLogin } = await getUser();
  return { firebaseUser, thinkoUser: user, firstTimeLogin };
}

/* ------------------------------------------------------------------ */
/* Auth bootstrap (PASSIVE) */
/* ------------------------------------------------------------------ */

function bootstrapAuth() {
  const auth = getAuthInstance();
  // ensure persistence set
  setPersistence(auth, browserLocalPersistence);

  // anonymous fallback handled by listener layer
}


/* ------------------------------------------------------------------ */
/* Google Sign-In (POPUP ONLY) */
/* ------------------------------------------------------------------ */
async function signInWithGoogle(deleteAnonymous?: boolean) {
  const auth = getAuthInstance();
  const provider = new GoogleAuthProvider();
  const currentUser = auth.currentUser;

  try {
    // Anonymous → Google (link)
    if (currentUser?.isAnonymous) {
      if (deleteAnonymous) {
        try {
          await currentUser.delete();
        } catch {
          await auth.signOut();
        }
        await signInWithPopup(auth, provider);
      } else {
        await linkWithPopup(currentUser, provider);
      }
    } else {
      // Normal Google sign-in
      await signInWithPopup(auth, provider);
    }
    return 'OK';
  } catch (e) {
    const err = e as AuthError;
    /**
     * Google account already exists
     * → Prefer Google user
     * → Remove anonymous user
     */
    if (err.code === "auth/credential-already-in-use") {
      if (deleteAnonymous && currentUser?.isAnonymous) {
        await currentUser.delete();
        await signInWithPopup(auth, provider);
        return "OK";
      }
      return "ACCOUNT_PRESENT";
    }
    /**
     * Popup issues → FAIL HARD
     * Caller must show UI message
     */
    if (
      err.code === "auth/popup-blocked" ||
      err.code === "auth/popup-closed-by-user"
    ) {
      return 'POPUP_BLOCKED';
    }
    throw err;
  }
}


/* ------------------------------------------------------------------ */
/* Firebase Token */
/* ------------------------------------------------------------------ */

async function getFirebaseToken(forceRefresh = false) {

  const auth = getAuthInstance();
  const user = auth?.currentUser;
  if (!user) return null;

  return await user.getIdToken(forceRefresh);
}

/* ------------------------------------------------------------------ */
/* Sign out */
/* ------------------------------------------------------------------ */
async function signOutUser() {
  const auth = getAuthInstance();
  await auth.signOut();
}


export {
  signOutUser,
  signInWithGoogle,
  getFirebaseToken,
  bootstrapAuth,
  initAuthListeners
};